/**
 * 3D occupancy for the endless void: cell (x, z) → occupied vertical bands.
 *
 * The void has no ground, so nothing is "the" height of a cell — a column may
 * carry a channel at y −3.25, an island slab from −1 to 0, and an older
 * channel at +1.5, all legal as long as bands don't overlap. New track claims
 * a band of CHANNEL_CLEARANCE above its base (walls + marble headroom);
 * islands claim their mass. Crossing over/under/around earlier track is
 * exactly "my band fits between yours".
 *
 * Bands carry a `ref` (e.g. "seg/12", "isle/3") so a disposed segment or
 * culled island can release everything it claimed — memory stays bounded by
 * the streaming window, never by how far the line has travelled.
 */

export interface Band {
  y0: number;
  y1: number;
  ref: string;
}

/** Vertical claim of a channel above its base: walls plus marble headroom.
 * Two stacked channels at Δy = 1.0 (the kit's bridge separation) just clear:
 * [y, y+0.99] vs [y+1, y+1.99]. */
export const CHANNEL_CLEARANCE = 0.99;

const key = (x: number, z: number) => `${x},${z}`;

/** What a planner needs of the occupancy — satisfied by both the real map
 * and a transaction overlay. */
export interface OccView {
  isFree(x: number, z: number, y0: number, y1: number): boolean;
  at(x: number, z: number): readonly Band[];
  claim(x: number, z: number, y0: number, y1: number, ref: string): void;
}

export class Occupancy implements OccView {
  private cells = new Map<string, Band[]>();
  /** ref → cell keys it claimed (for O(claims) release). */
  private byRef = new Map<string, string[]>();

  /** True when [y0, y1] overlaps no existing band at (x, z). */
  isFree(x: number, z: number, y0: number, y1: number): boolean {
    const bands = this.cells.get(key(x, z));
    if (!bands) return true;
    for (const b of bands) {
      if (y0 < b.y1 && b.y0 < y1) return false;
    }
    return true;
  }

  /** Bands at a cell (empty array if none). */
  at(x: number, z: number): readonly Band[] {
    return this.cells.get(key(x, z)) ?? [];
  }

  claim(x: number, z: number, y0: number, y1: number, ref: string): void {
    const k = key(x, z);
    const bands = this.cells.get(k) ?? [];
    bands.push({ y0, y1, ref });
    this.cells.set(k, bands);
    const keys = this.byRef.get(ref) ?? [];
    keys.push(k);
    this.byRef.set(ref, keys);
  }

  /** Claim a channel band: base y plus walls + marble headroom. */
  claimChannel(x: number, z: number, baseY: number, ref: string): void {
    this.claim(x, z, baseY, baseY + CHANNEL_CLEARANCE, ref);
  }

  /** Release every band claimed under `ref`. */
  release(ref: string): void {
    const keys = this.byRef.get(ref);
    if (!keys) return;
    this.byRef.delete(ref);
    for (const k of keys) {
      const bands = this.cells.get(k);
      if (!bands) continue;
      const kept = bands.filter((b) => b.ref !== ref);
      if (kept.length === 0) this.cells.delete(k);
      else this.cells.set(k, kept);
    }
  }

  /** Every band claimed under `ref`, with its cell coordinates. Keys are
   * deduped: a cell claimed twice under one ref (island mass + canopy)
   * must not report its bands twice — that duplicated every island block
   * placement (found via doubled collision boxes). */
  claimsOf(ref: string): { x: number; z: number; y0: number; y1: number }[] {
    const keys = new Set(this.byRef.get(ref) ?? []);
    const out: { x: number; z: number; y0: number; y1: number }[] = [];
    for (const k of keys) {
      const [x, z] = k.split(",").map(Number);
      for (const b of this.cells.get(k) ?? []) {
        if (b.ref === ref) out.push({ x, z, y0: b.y0, y1: b.y1 });
      }
    }
    return out;
  }

  /** Claimed cell count — bounded-memory sanity in tests. */
  get size(): number {
    return this.cells.size;
  }
}

/**
 * Write overlay over a base Occupancy: planning a segment claims here; a
 * stuck plan is discarded wholesale (nothing touched the base), a finished
 * plan commits its claims through. Cheap — O(claims of one segment).
 */
export class OccupancyTx implements OccView {
  private added: {
    x: number;
    z: number;
    y0: number;
    y1: number;
    ref: string;
  }[] = [];
  private byCell = new Map<string, Band[]>();

  /** Base may itself be a transaction — probe plans stack a second layer. */
  constructor(private readonly base: OccView) {}

  isFree(x: number, z: number, y0: number, y1: number): boolean {
    if (!this.base.isFree(x, z, y0, y1)) return false;
    const bands = this.byCell.get(key(x, z));
    if (!bands) return true;
    for (const b of bands) {
      if (y0 < b.y1 && b.y0 < y1) return false;
    }
    return true;
  }

  at(x: number, z: number): readonly Band[] {
    const mine = this.byCell.get(key(x, z));
    const base = this.base.at(x, z);
    return mine ? [...base, ...mine] : base;
  }

  claim(x: number, z: number, y0: number, y1: number, ref: string): void {
    this.added.push({ x, z, y0, y1, ref });
    const k = key(x, z);
    const bands = this.byCell.get(k) ?? [];
    bands.push({ y0, y1, ref });
    this.byCell.set(k, bands);
  }

  /** Flush every overlay claim into the base map, refs preserved. */
  commit(): void {
    for (const c of this.added) {
      this.base.claim(c.x, c.z, c.y0, c.y1, c.ref);
    }
    this.added = [];
    this.byCell.clear();
  }
}
